import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import './field.css'

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode
  /** Validation message. Its presence is what marks the field invalid. */
  error?: ReactNode
  /** Persistent helper text, shown when there is no error. */
  hint?: ReactNode
}

export function Field({ label, error, hint, className, ...rest }: FieldProps) {
  const id = useId()
  const messageId = `${id}-message`
  const invalid = Boolean(error)

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className={cx('input', className)}
        aria-invalid={invalid || undefined}
        // Point at whichever message is actually rendered, so screen readers announce the
        // validation text the sighted user is reading.
        aria-describedby={error || hint ? messageId : undefined}
        {...rest}
      />
      {error ? (
        // Assertive enough to be announced when it appears, without stealing focus.
        <div id={messageId} className="field-message" role="alert">
          {error}
        </div>
      ) : hint ? (
        <div id={messageId} className="field-hint">
          {hint}
        </div>
      ) : null}
    </div>
  )
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode
}

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={cx('check', className)}>
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  )
}
